import { Buton, cn, Kart } from "@koclukpanel/ui";
import { SAAT_DILIMI } from "@koclukpanel/contracts";
import type { DenemeOzeti } from "./api";

/**
 * "7 Ekim 09:30" — F-EXM-01a saatli pencere. Saat dilimi ACIK: telefonun
 * dilimi yanlissa ogrenci acilis saatini kaydirilmis gorurdu.
 */
function anMetni(t: string): string {
  return new Date(t).toLocaleString("tr-TR", {
    timeZone: SAAT_DILIMI,
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface DenemeListesiProps {
  denemeler: DenemeOzeti[];
  onKodla: (deneme: DenemeOzeti) => void;
  onYenile: () => void;
  yukleniyor?: boolean;
}

/**
 * DENEME LISTESI — F-EXM-04
 *
 * ================================================================
 * PENCERE DURUMU SUNUCUDAN GELIYOR
 * ================================================================
 * "Cevap girisi acik mi" sorusunu arayuz kendi saatine gore
 * hesaplamiyor. Hesaplasaydi, saati yanlis olan bir telefonda ogrenci
 * "acik" gorup gonderdiginde sunucudan hata alirdi — ve hatayi
 * kendisinin sanırdi.
 */
const DURUM: Record<DenemeOzeti["pencere"], { etiket: string; sinif: string }> = {
  /*
    Renk TEK BASINA anlam tasimiyor — her durumun metni var.

    METIN RENGI `onay-700`: `basarili-500` beyaz uzerinde 3,02
    olculdu ve AA normal metin 4,5 istiyor. 3,0 esigi metin OLMAYAN
    ogeler icin; buradaki rozetin ICINDE okunacak bir metin var.
  */
  ACIK: {
    etiket: "Cevap girişi açık",
    sinif: "bg-basarili-500/10 text-onay-700 ring-basarili-500/20",
  },
  HENUZ_ACILMADI: { etiket: "Henüz açılmadı", sinif: "bg-notr-100 text-notr-600 ring-notr-300" },
  KAPANDI: { etiket: "Süre doldu", sinif: "bg-notr-100 text-notr-600 ring-notr-300" },
};

export function DenemeListesi({ denemeler, onKodla, onYenile, yukleniyor }: DenemeListesiProps) {
  if (denemeler.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        {/*
          ================================================================
          BOS EKRAN NE OLACAGINI SOYLUYOR — 07.09.2026
          ================================================================
          Once yalnizca "Sana atanmis deneme yok." yaziyordu. Dogruydu
          ama cikmaz sokakti: ogrenci bunu okuyup "ben mi bir sey
          yapmaliyim?" diye dusunuyor ve yapabilecegi bir sey de yok.

          Uygulamanin diger bos durumlari ne olacagini soyluyor
          ("Cozemedigin soru kalmamis", "Bu haftanin programi
          hazirlanmamis"); bu ekran soylemiyordu.
        */}
        <p className="text-notr-900 text-sm font-medium">Henüz deneme yok</p>
        <p className="text-notr-600 max-w-xs text-sm">
          Denemeleri koçun atar. Bir deneme atandığında burada görünür ve sonucunu buradan girersin.
        </p>
        <Buton gorunum="sessiz" onClick={onYenile} yukleniyor={yukleniyor}>
          Yenile
        </Buton>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-notr-600 text-sm">{denemeler.length} deneme</p>
        <Buton gorunum="sessiz" onClick={onYenile} yukleniyor={yukleniyor}>
          Yenile
        </Buton>
      </div>

      <ul className="flex flex-col gap-2">
        {denemeler.map((d) => {
          const durum = DURUM[d.pencere];
          // Girisi ACIK olan bir denemeye SONUC GIRILMIS olsa bile
          // tekrar girilebiliyor: ogrenci kodlarken yanlis
          // isaretlediyse duzeltebilmeli. Pencere kapaninca kilitleniyor.
          const kodlanabilir = d.pencere === "ACIK";

          return (
            <Kart key={d.atamaId} olarak="li" className="flex flex-col gap-2 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-notr-900 truncate text-sm font-medium">{d.name}</p>
                  <p className="text-notr-600 text-xs">
                    {new Date(d.examDate).toLocaleDateString("tr-TR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}{" "}
                    · {d.kind}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                      durum.sinif,
                    )}
                  >
                    {durum.etiket}
                  </span>
                  {/*
                    UZMAN "GIRMEDIN" ISARETLEDIYSE SOYLENIYOR.

                    Bu bilgi 04.09.2026'ya kadar TELE HIC CIKMIYORDU
                    (C14.6): sunucu `participation`i sorguluyor ama
                    yanita koymuyordu. Sonucu, ogrencinin listesinde
                    "uzman girmedin dedi" ile "henuz kodlamadim" AYNI
                    gorunuyordu — ve ikisi arasindaki fark, karnesine
                    girip girmeyecegi.
                  */}
                  {d.katilim === "MISSED" && (
                    <span className="bg-uyari-500/15 text-uyari-600 rounded-full px-2 py-0.5 text-xs font-medium">
                      Girmedin olarak işaretli
                    </span>
                  )}
                </div>
              </div>

              {/* Sonuc girilmisse SAYILAR gosteriliyor ama cevap
                  anahtari YOK (F-EXM-12) — o, sure dolunca aciliyor. */}
              {d.girildiMi && d.sonuc && (
                <p className="text-notr-700 text-sm">
                  {d.sonuc.totalCorrect}D · {d.sonuc.totalWrong}Y · {d.sonuc.totalEmpty}B ·{" "}
                  <span className="font-medium">
                    {Math.round(d.sonuc.totalNet * 100) / 100} net
                  </span>
                </p>
              )}

              {kodlanabilir && (
                <p className="text-notr-600 text-xs">Son giriş: {anMetni(d.endsAt)}</p>
              )}

              {kodlanabilir ? (
                <Buton boyut="genis" onClick={() => onKodla(d)}>
                  {d.girildiMi ? "Cevaplarımı düzelt" : "Cevaplarımı gir"}
                </Buton>
              ) : (
                <p className="text-notr-600 text-xs">
                  {d.pencere === "HENUZ_ACILMADI"
                    ? `Giriş ${anMetni(d.startsAt)} tarihinde açılıyor.`
                    : d.girildiMi
                      ? "Süre doldu, cevapların kaydedildi."
                      : "Süre doldu. Cevaplarını uzmanına ilet."}
                </p>
              )}

              {d.pdfUrl && (
                <a
                  href={d.pdfUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-marka-700 text-sm underline"
                >
                  Kitapçık PDF
                </a>
              )}
            </Kart>
          );
        })}
      </ul>
    </div>
  );
}
